import React, { RefObject, useEffect, useState } from 'react';
import Button from '../../atoms/button';
import IconButton from '../../atoms/iconButton/IconButton';
import { CarretArrowDownJAG, MessageStroke1 } from '../../atoms/icons';
import PrimaryLoader from '../../atoms/primaryLoader/PrimaryLoader';
import LinkToWishlist from '@srcDS/components/molecules/linkToWishlist/LinkToWishlist';
import { Select } from '../../atoms/select/Select';
import { Text } from '../../typography/text/Text';
import { Image as CustomImage } from '../../atoms/image/Image';
import Price from '../price/Price';
import styles from './StickyAddTo.module.scss';
// @ts-ignore
import emptyImage from './emptyImage.jpg';
import { FORM_TYPE } from '../account/accountDropdown/AccountDropdown';
import { useOpenSignForm } from '@srcDS/utils/useOpenSignForm';

import { getPrices } from '@srcDS/utils/helpers';
import showLoginErrorMessage from '../../../utils/showLoginErrorMessage';

import { selectCustomeStyles, theme } from './options';
import { useMedia } from 'react-use';
import { useLayoutContext } from '@srcDS/provider/layout/LayoutContext';

type Variant = {
  sku: string;
  lineItemId: string;
  projectSpecificData: {
    prices?: { price: number; discountedPrice?: number; onSale?: boolean }[];
  };
};

type Option = {
  value: string;
  label: string;
  availableQuantity: number;
  mobileLabel?: string;
  attributes?: {
    alphasize?: string;
  };
};

type StickyAddToProps = {
  setValue: (name: string, value: string | number) => void;
  image?: string;
  colour: string;
  onAddToCart: (variant: Variant, quantity: number) => Promise<void>;
  actionButtonRef?: RefObject<HTMLDivElement>;
  variants?: Option[];
  selectVariant: (variant: Option) => void;
  setActiveOption: (variant: Option) => void;
  variant: Variant;
  register: any; // Adjust this type if using a specific form library
  selectedQty: number;
  onOpenChat: () => void;
  onCloseChat: () => void;
  onChangeVariant: (variant: Option) => void;
  title: string;
  isSelectingSize: boolean;
  productId: string;
  productName: string;
  displayAlphaSize?: boolean;
};

const customArgs = {
  label: '',
  variant: 'normal',
  labelPosition: 'normal',
};

export const StickyAddTo: React.FC<StickyAddToProps> = ({
  setValue,
  image,
  colour,
  onAddToCart,
  actionButtonRef,
  variants,
  selectVariant,
  setActiveOption,
  variant,
  register,
  selectedQty,
  onOpenChat,
  onCloseChat,
  onChangeVariant,
  title,
  isSelectingSize,
  productId,
  productName,
  displayAlphaSize,
}) => {
  const { signFormOpen } = useOpenSignForm();
  const { setAlwaysHideLiveChat } = useLayoutContext();
  const isMobile = useMedia('(max-width: 979px)', false);
  const [isStickyShown, setStickyVisibility] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [statusMenu, setStatusMenu] = useState({ status: false, label: false });
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const defaultValue = isSelectingSize ? null : variant.sku;

  const {
    projectSpecificData: { prices = [] },
  } = variant;

  if (isMobile) {
    setAlwaysHideLiveChat(true);
  }

  const { price: regularPrice, discountedPrice, onSale } = getPrices(prices);

  useEffect(() => {
    if (actionButtonRef?.current) {
      const cachedRef = actionButtonRef.current;
      const observer = new IntersectionObserver(
        ([e]) => {
          setStickyVisibility(e.intersectionRatio < 0.1);
        },
        { threshold: [0.1] },
      );

      observer.observe(cachedRef);
      return () => {
        observer.unobserve(cachedRef);
      };
    }
  }, [actionButtonRef]);

  const setQty = (availableQuantity: number) => {
    if (isMobile) return null;
    if (availableQuantity <= 0) {
      return (
        <Text weight="light" size="xs" className={styles.outOfStockLabel}>
          Out of stock
        </Text>
      );
    } else if (availableQuantity > 0 && availableQuantity <= 10) {
      return (
        <Text weight="light" size="xs">
          {`Only ${availableQuantity} left`}
        </Text>
      );
    }
    return null;
  };

  const transformedIndexText = () => {
    const textIndex = `Colour: ${colour}`;
    if (theme === 'Sportscraft') {
      return textIndex
        .split(' ')
        .map((el) => el[0].toUpperCase() + el.slice(1).toLowerCase())
        .join(' ');
    }
    return textIndex;
  };

  return (
    <div style={{ display: isStickyShown ? 'flex' : 'none' }} className={styles.wrapper}>
      <div className={styles.productBlock}>
        {!image ? (
          /*@ts-ignore*/
          <img src={emptyImage} className={styles.productImage} alt={title} />
        ) : (
          <CustomImage
            alt={title}
            /*@ts-ignore*/
            title={title}
            itemProp="image"
            className={styles.productImage}
            image={image}
            opts={{
              crop: 'fill',
              width: 88,
              height: 130,
              quality: 'auto',
            }}
          />
        )}

        <div className={styles.textBlock}>
          <div>
            <Text weight="light" className={styles.productNameText} size="xs" as="p">
              {title}
            </Text>
            <Text weight="light" className={styles.colorTextMobile} size="xxs" as="span">
              {transformedIndexText()}
            </Text>
          </div>

          <Text weight="light" className={styles.colorTextDesktop} size="xxs" as="span">
            {transformedIndexText()}
          </Text>
          <div className={styles.priceTextBlock}>
            {regularPrice && regularPrice !== 0 && discountedPrice ? (
              <Price
                placement="widget"
                type="regular"
                onSale={!!discountedPrice && discountedPrice !== regularPrice}
                value={regularPrice}
              />
            ) : (
              <Price placement="widget" type="regular" value={regularPrice} />
            )}
            {discountedPrice && discountedPrice !== regularPrice && (
              <Price placement="widget" type={onSale ? 'sale' : 'regular'} value={discountedPrice} />
            )}
          </div>
        </div>
      </div>
      <div className={styles.bottomBlock}>
        <div className={styles.priceBottomBlock}>
          {regularPrice && regularPrice !== 0 && discountedPrice ? (
            <Price
              placement="widget"
              type="regular"
              onSale={!!discountedPrice && discountedPrice !== regularPrice}
              value={regularPrice}
            />
          ) : (
            <Price placement="widget" type="regular" value={regularPrice} />
          )}
          {discountedPrice && discountedPrice !== regularPrice && (
            <Price placement="widget" type={onSale ? 'sale' : 'regular'} value={discountedPrice} />
          )}
        </div>
        {variants && (
          <Select
            openToTop={isMobile}
            register={register}
            name="size1"
            /*@ts-ignore*/
            isPlaceholder={true}
            defaultValue={defaultValue}
            className={styles.customSelect}
            customClasses={selectCustomeStyles}
            defaultExpaned={statusMenu}
            customIcon={theme === 'Jag' && CarretArrowDownJAG}
            onChange={(event) => {
              let selected;
              selected = variants.find((el) => el.value === event.target.value);

              setValue('size', event.target.value);
              setValue('size2', event.target.value);
              setValue('size3', event.target.value);

              onChangeVariant(selected);
              selectVariant(selected);
              setActiveOption(selected);
            }}
            selectAriaLabel="Sticky Size selector"
            {...customArgs}
          >
            {variants &&
              variants.map(({ value: val, label, availableQuantity, mobileLabel, attributes }, index) => {
                return (
                  <Select.Option
                    ariaLabel={label}
                    selected={val === defaultValue}
                    key={label + val}
                    index={index}
                    label={isMobile ? mobileLabel || label : label}
                    value={val}
                    isOutOfStock={availableQuantity <= 0}
                    htmlContent={
                      displayAlphaSize ? (
                        <div className={styles.selectListItemContent}>
                          <div className={styles.selectListItemLabel}>
                            <span className={availableQuantity <= 0 ? styles.outOfStockText : ''}>{label}</span>
                            {setQty(availableQuantity > 0 ? availableQuantity : 0)}
                          </div>
                          {attributes?.alphasize && <span className={styles.alphaSize}>{attributes?.alphasize}</span>}
                        </div>
                      ) : (
                        <>
                          <span>{label}</span>
                          {setQty(availableQuantity > 0 ? availableQuantity : 0)}
                        </>
                      )
                    }
                  />
                );
              })}
          </Select>
        )}
        {/*@ts-ignore*/}
        <Button
          disableTouchEvent
          variant="primary"
          className={styles.btn}
          size="large"
          disabled={isAddingToCart}
          onClick={() => {
            if (isSelectingSize) {
              return setStatusMenu((prev) => ({
                status: true,
                label: !prev.label,
              }));
            }
            setIsAddingToCart(true);
            return onAddToCart(variant, Number(selectedQty))
              .then(() => setIsAddingToCart(false))
              .catch(() => setIsAddingToCart(false));
          }}
        >
          {isSelectingSize ? (
            'Select a size'
          ) : isAddingToCart ? (
            <PrimaryLoader mode="segment" theme="light" size={20} />
          ) : (
            'Add to bag'
          )}
        </Button>
        <LinkToWishlist
          item={{ productName, lineItemId: productId, variant }}
          lineItemId={variant.lineItemId}
          firstSkuId={variant.sku}
          toSignUp={() => {
            signFormOpen(FORM_TYPE.LOGIN);
            showLoginErrorMessage();
          }}
          setLoading={setLoading}
          loading={loading}
          isIcon
          customClasses={{
            linkToWishlist: styles.iconButton,
          }}
        />

        <IconButton
          /*@ts-ignore*/
          className={styles.iconMessage}
          color="primary"
          variant="primary"
          iconRegular={() => <MessageStroke1 width="30" height="30" />}
          iconHover={() => <MessageStroke1 width="30" height="30" />}
          iconActive={() => <MessageStroke1 width="30" height="30" />}
          onClick={() => {
            if (isChatOpen) {
              onCloseChat();
              setIsChatOpen(false);
            } else {
              onOpenChat();
              setIsChatOpen(true);
            }
          }}
        />
      </div>
    </div>
  );
};
